import React, { useEffect, useMemo, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import {
  fetchDashboardStatsAsync,
  fetchWeeklyScheduleAsync,
  fetchStudentVacationsWeeklyAsync,
  fetchScheduledConsultationsAsync,
  fetchMonthlyRevenueAsync,
  REVENUE_YEAR_MIN,
  ScheduledConsultation,
  MonthlyRevenuePoint,
  RevenuePrediction,
} from '../store/slices/dashboardSlice';
import axios from 'axios';
import LoadingSpinner from './LoadingSpinner';
import HoverTooltip from './Tooltip';
import TalkalystNudge from './TalkalystNudge';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
} from 'recharts';
import { buildStackedPredictionRows } from '../utils/monthlyBarChart';
import { buildDayLessons } from '../utils/weeklySchedule';
import './Dashboard.css';

const API_BASE_URL = import.meta.env.VITE_API_URL as string;

const DAYS_ORDER = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;
const DAY_LABELS: Record<string, string> = {
  monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed',
  thursday: 'Thu', friday: 'Fri', saturday: 'Sat', sunday: 'Sun',
};

function getWeekDates(): { day: string; date: Date; label: string; dateLabel: string }[] {
  const now = new Date();
  const jsDay = now.getDay(); // 0=Sun
  const mondayOffset = jsDay === 0 ? -6 : 1 - jsDay;
  const monday = new Date(now);
  monday.setDate(now.getDate() + mondayOffset);
  monday.setHours(0, 0, 0, 0);

  return DAYS_ORDER.map((day, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return {
      day,
      date: d,
      label: DAY_LABELS[day],
      dateLabel: d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }),
    };
  });
}

function isToday(date: Date): boolean {
  const now = new Date();
  return date.getDate() === now.getDate() &&
    date.getMonth() === now.getMonth() &&
    date.getFullYear() === now.getFullYear();
}

function formatDateISO(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** 12 calendar months for selected year (revenue API is available from 2026). */
function buildMonthlyRevenueChartData(
  series: MonthlyRevenuePoint[] | null,
  year: number,
  prediction: RevenuePrediction | null,
) {
  const points = (series || []).map((p) => ({
    month: p.month,
    value: parseFloat(p.revenue),
  }));
  return buildStackedPredictionRows(points, year, prediction).map((row) => ({
    monthKey: row.monthKey,
    monthLabel: row.monthLabel,
    revenue: row.value,
    predicted: row.predicted,
    predictedTotal: row.predictedTotal,
  }));
}

interface RevenueTooltipRow { revenue: number; predicted: number; predictedTotal: number }
interface RevenueTooltipProps {
  active?: boolean;
  label?: string;
  payload?: Array<{ payload: RevenueTooltipRow }>;
}

/** Tooltip: collected revenue, plus a "Predicted" line showing the projected month total
 *  (collected + still-to-come) — shown only when there's a non-zero prediction. */
const RevenueTooltip: React.FC<RevenueTooltipProps> = ({ active, label, payload }) => {
  if (!active || !payload || payload.length === 0) return null;
  const row = payload[0].payload;
  return (
    <div className="revenue-tooltip">
      <div className="revenue-tooltip__label">{label}</div>
      <div className="revenue-tooltip__row revenue-tooltip__row--collected">
        Collected: {row.revenue.toFixed(2)} BYN
      </div>
      {row.predicted > 0 && (
        <div className="revenue-tooltip__row revenue-tooltip__row--predicted">
          Predicted: {row.predictedTotal.toFixed(2)} BYN
        </div>
      )}
    </div>
  );
};

const Dashboard: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const {
    stats,
    loading,
    error,
    weeklyGroups,
    existingLessons,
    vacationDates,
    weeklyLoading,
    studentVacations,
    scheduledConsultations,
    monthlyRevenue,
    monthlyRevenuePrediction,
    monthlyRevenueLoading,
    monthlyRevenueError,
  } = useAppSelector((state) => state.dashboard);

  const [revenueYear, setRevenueYear] = useState(() => Math.max(REVENUE_YEAR_MIN, new Date().getFullYear()));

  const token = useAppSelector((state) => state.auth?.token);
  const [expiringCritical, setExpiringCritical] = useState<number | null>(null);

  useEffect(() => {
    dispatch(fetchDashboardStatsAsync());
    dispatch(fetchWeeklyScheduleAsync());
    dispatch(fetchStudentVacationsWeeklyAsync());
    dispatch(fetchScheduledConsultationsAsync());
  }, [dispatch]);

  // Critical (≤1 lesson left, or expired) count for the widget — self-contained to avoid
  // coupling into dashboardSlice; falls back to 0 on error.
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API_BASE_URL}/subscriptions/expiring?filter=critical`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      })
      .then((res) => { if (!cancelled) setExpiringCritical(res.data?.meta?.critical ?? 0); })
      .catch(() => { if (!cancelled) setExpiringCritical(0); });
    return () => { cancelled = true; };
  }, [token]);

  useEffect(() => {
    dispatch(fetchMonthlyRevenueAsync(revenueYear));
  }, [dispatch, revenueYear]);

  const weekDates = useMemo(() => getWeekDates(), []);
  const dayLessons = useMemo(() => {
    const datesByDay: Record<string, string> = {};
    for (const { day, date } of weekDates) datesByDay[day] = formatDateISO(date);
    return buildDayLessons(weeklyGroups, datesByDay);
  }, [weeklyGroups, weekDates]);
  const existingSet = useMemo(() => {
    const s = new Set<string>();
    for (const el of existingLessons) s.add(`${el.groupId}:${el.date}`);
    return s;
  }, [existingLessons]);
  const canceledSet = useMemo(() => {
    const s = new Set<string>();
    for (const el of existingLessons) {
      if (el.canceled) s.add(`${el.groupId}:${el.date}`);
    }
    return s;
  }, [existingLessons]);
  const vacationByDate = useMemo(() => {
    const m = new Map<string, boolean>();
    for (const v of vacationDates) m.set(v.date, v.isIllness);
    return m;
  }, [vacationDates]);
  const consultationsByDate = useMemo(() => {
    const m = new Map<string, ScheduledConsultation[]>();
    for (const c of scheduledConsultations) {
      const arr = m.get(c.consultationDate) || [];
      arr.push(c);
      m.set(c.consultationDate, arr);
    }
    for (const arr of m.values()) {
      arr.sort((a, b) => {
        if (a.consultationTime && b.consultationTime) return a.consultationTime.localeCompare(b.consultationTime);
        if (a.consultationTime) return -1;
        if (b.consultationTime) return 1;
        return a.id - b.id;
      });
    }
    return m;
  }, [scheduledConsultations]);

  // Position-aligned grid:
  //   1) Top rows: untimed consultations (positional, one row per max count across days).
  //   2) Below: a single time-sorted row per unique HH:MM across the week —
  //      a timed consultation and a lesson at the same time share a row.
  const allTimes = useMemo(() => {
    const s = new Set<string>();
    for (const day of DAYS_ORDER) {
      for (const slot of dayLessons[day] || []) s.add(slot.time.slice(0, 5));
    }
    for (const c of scheduledConsultations) {
      if (c.consultationTime) s.add(c.consultationTime.slice(0, 5));
    }
    return Array.from(s).sort();
  }, [dayLessons, scheduledConsultations]);

  const maxUntimedConsultations = useMemo(() => {
    let max = 0;
    for (const arr of consultationsByDate.values()) {
      const untimed = arr.filter(c => !c.consultationTime).length;
      if (untimed > max) max = untimed;
    }
    return max;
  }, [consultationsByDate]);

  const revenueChartData = useMemo(
    () => buildMonthlyRevenueChartData(monthlyRevenue, revenueYear, monthlyRevenuePrediction),
    [monthlyRevenue, revenueYear, monthlyRevenuePrediction]
  );

  const goRevenueYearPrev = useCallback(() => {
    setRevenueYear((y) => Math.max(REVENUE_YEAR_MIN, y - 1));
  }, []);

  const goRevenueYearNext = useCallback(() => {
    setRevenueYear((y) => y + 1);
  }, []);

  const handleLessonClick = useCallback((date: Date, groupId: number) => {
    navigate(`/lesson?date=${formatDateISO(date)}&group=${groupId}`);
  }, [navigate]);

  return (
    <div className="dashboard">
      <header className="dashboard-page-header">
        <h1>Dashboard</h1>
      </header>
      
      <main className="dashboard-content">
        {loading && <LoadingSpinner />}
        {error && <div className="error-message">{error}</div>}

        <TalkalystNudge />
        
        <div className="dashboard-grid">
          <div className="dashboard-card dashboard-card-clickable" onClick={() => navigate('/groups')}>
            <h3>Active Groups</h3>
            <div className="card-stats">
              <span className="stat-number">{stats?.activeGroups ?? 0}</span>
              <span className="stat-label">Active Groups</span>
            </div>
          </div>

          <div className="dashboard-card dashboard-card-clickable" onClick={() => navigate('/students')}>
            <h3>Active Students</h3>
            <div className="card-stats">
              <div className="stat-number-row">
                <span className="stat-number">{stats?.activeStudents ?? 0}</span>
                {(stats?.riskyStudents ?? 0) > 0 && (
                  <HoverTooltip
                    text={`${stats?.riskyStudents} student(s) flagged as at risk of leaving (${stats?.riskyCertain ?? 0} certain, ${stats?.riskyModerate ?? 0} moderate) — still counted as active, but watch for churn.`}
                  >
                    <span
                      className="risk-bubble"
                      onClick={(e) => { e.stopPropagation(); navigate('/students'); }}
                    >
                      {stats?.riskyStudents}
                    </span>
                  </HoverTooltip>
                )}
              </div>
              <span className="stat-label">Active Students</span>
            </div>
          </div>

          <div className="dashboard-card dashboard-card-clickable" onClick={() => navigate('/absences')}>
            <h3>Absences</h3>
            <div className="card-stats">
              <span className="stat-number">{stats?.absencesThisWeek ?? 0}</span>
              <span className="stat-label">This Week</span>
            </div>
          </div>

          <div className="dashboard-card dashboard-card-clickable" onClick={() => navigate('/vocabulary')}>
            <h3>Vocabulary</h3>
            <div className="card-stats">
              <span className="stat-number">{stats?.vocabularyThisMonth ?? 0}</span>
              <span className="stat-label">This Month</span>
            </div>
          </div>

          <div className="dashboard-card dashboard-card-clickable" onClick={() => navigate('/contacts')}>
            <h3>New Contacts</h3>
            <div className="card-stats">
              <span className={`stat-number${(stats?.newContactsCount ?? 0) > 0 ? ' stat-number-alert' : ''}`}>{stats?.newContactsCount ?? 0}</span>
              <span className="stat-label">Pending</span>
            </div>
          </div>

          <div className="dashboard-card dashboard-card-clickable" onClick={() => navigate('/expiring-subscriptions?filter=critical')}>
            <h3>Expiring Subs</h3>
            <div className="card-stats">
              <span className={`stat-number${(expiringCritical ?? 0) > 0 ? ' stat-number-alert' : ''}`}>{expiringCritical ?? 0}</span>
              <span className="stat-label">Expiring</span>
            </div>
          </div>
        </div>
        
        <div className="weekly-calendar">
          <h2 className="weekly-calendar-title">This Week's Schedule</h2>
          {weeklyLoading ? (
            <LoadingSpinner />
          ) : (
            <div className="week-grid">
              {weekDates.map(({ day, date, label, dateLabel }) => {
                const today = isToday(date);
                const dateISO = formatDateISO(date);
                const isVacation = vacationByDate.has(dateISO);
                const isIllness = vacationByDate.get(dateISO) === true;
                const lessons = dayLessons[day] || [];
                const lessonsByTime = new Map(lessons.map(l => [l.time.slice(0, 5), l]));
                const consultations = consultationsByDate.get(dateISO) || [];
                const untimedConsultations = consultations.filter(c => !c.consultationTime);
                const timedConsultationsByTime = new Map<string, ScheduledConsultation>();
                for (const c of consultations) {
                  if (c.consultationTime) timedConsultationsByTime.set(c.consultationTime.slice(0, 5), c);
                }
                const isEmpty = !isVacation
                  && maxUntimedConsultations === 0
                  && allTimes.length === 0;
                return (
                  <div key={day} className={`week-day-column${today ? ' today' : ''}${isVacation ? ' vacation' : ''}${isIllness ? ' illness' : ''}`}>
                    <div className="week-day-header">
                      <span className="week-day-name">{label}</span>
                      <span className={`week-day-date${today ? ' today-date' : ''}`}>{dateLabel}</span>
                    </div>
                    <div className="week-day-lessons">
                      {isVacation ? (
                        <div className="vacation-label">{isIllness ? '🌡️ Sick' : '🏖️ Off'}</div>
                      ) : isEmpty ? (
                        <div className="no-lessons">—</div>
                      ) : (
                        <>
                          {Array.from({ length: maxUntimedConsultations }).map((_, i) => {
                            const c = untimedConsultations[i];
                            if (!c) {
                              return <div key={`cph-${i}`} className="consultation-chip placeholder" aria-hidden="true" />;
                            }
                            return (
                              <div
                                key={`c-${c.id}`}
                                className="consultation-chip"
                                onClick={() => navigate(`/consultations/edit/${c.id}`)}
                                title={c.hasRecording ? 'Consultation (has recording)' : 'Consultation (no recording)'}
                              >
                                <span className="lesson-group">
                                  {c.hasRecording ? '🎙️' : '💬'} {[c.firstName, c.lastName].filter(Boolean).join(' ') || 'Consultation'}
                                </span>
                              </div>
                            );
                          })}
                          {allTimes.map(time => {
                            const slot = lessonsByTime.get(time);
                            const consultation = timedConsultationsByTime.get(time);
                            if (!slot && !consultation) {
                              return <div key={`ph-${time}`} className="lesson-chip placeholder" aria-hidden="true" />;
                            }
                            return (
                              <React.Fragment key={`row-${time}`}>
                                {consultation && (
                                  <div
                                    className="consultation-chip"
                                    onClick={() => navigate(`/consultations/edit/${consultation.id}`)}
                                    title={consultation.hasRecording ? 'Consultation (has recording)' : 'Consultation (no recording)'}
                                  >
                                    <span className="lesson-time">{consultation.consultationTime!.slice(0, 5)}</span>
                                    <span className="lesson-group">
                                      {consultation.hasRecording ? '🎙️' : '💬'} {[consultation.firstName, consultation.lastName].filter(Boolean).join(' ') || 'Consultation'}
                                    </span>
                                  </div>
                                )}
                                {slot && (
                                  <div
                                    className={`lesson-chip level-${slot.group.level}${existingSet.has(`${slot.group.id}:${dateISO}`) ? ' exists' : ''}${canceledSet.has(`${slot.group.id}:${dateISO}`) ? ' canceled' : ''}`}
                                    onClick={() => handleLessonClick(date, slot.group.id)}
                                    title={canceledSet.has(`${slot.group.id}:${dateISO}`) ? 'Canceled — does not consume subscription lessons' : undefined}
                                  >
                                    <span className="lesson-time">{slot.time.slice(0, 5)}</span>
                                    <span className="lesson-group">
                                      {slot.group.name || slot.group.level.toUpperCase()}
                                    </span>
                                  </div>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="dashboard-card dashboard-revenue-section">
          <div className="dashboard-revenue-header">
            <h3>Monthly Revenue (Subscriptions)</h3>
            <div className="dashboard-revenue-year-nav" aria-label="Select year">
              <button
                type="button"
                className="dashboard-revenue-year-btn"
                onClick={goRevenueYearPrev}
                disabled={revenueYear <= REVENUE_YEAR_MIN}
                aria-label="Previous year"
              >
                ←
              </button>
              <span className="dashboard-revenue-year-label">{revenueYear}</span>
              <button
                type="button"
                className="dashboard-revenue-year-btn"
                onClick={goRevenueYearNext}
                aria-label="Next year"
              >
                →
              </button>
            </div>
          </div>
          {monthlyRevenueError && <div className="error-message">{monthlyRevenueError}</div>}
          {monthlyRevenueLoading ? (
            <div className="dashboard-revenue-chart">
              <LoadingSpinner />
            </div>
          ) : (
            <div className="dashboard-revenue-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={revenueChartData} margin={{ top: 8, right: 12, left: 8, bottom: 56 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="monthLabel"
                    tick={{ fontSize: 11 }}
                    angle={-32}
                    textAnchor="end"
                    height={52}
                    interval={0}
                  />
                  <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => (Number.isInteger(v) ? String(v) : v.toFixed(0))} />
                  <Tooltip content={<RevenueTooltip />} />
                  <Bar dataKey="revenue" stackId="rev" fill="#667eea" name="Collected" radius={[4, 4, 0, 0]} />
                  <Bar
                    dataKey="predicted"
                    stackId="rev"
                    fill="#667eea"
                    fillOpacity={0.28}
                    stroke="#667eea"
                    strokeDasharray="4 3"
                    name="Predicted"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
        {/* Student Vacations This Week */}
        {studentVacations.length > 0 && (
          <div className="student-vacations-week">
            <h2 className="weekly-calendar-title">Student Vacations This Week</h2>
            <div className="vacation-list">
              {studentVacations.map(v => (
                <div key={v.id} className="vacation-row">
                  <span className="vacation-student">
                    {v.student.firstName} {v.student.lastName}{v.student.surname ? ` ${v.student.surname}` : ''}
                  </span>
                  <span className="vacation-period">
                    {new Date(v.dateFrom).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                    {' — '}
                    {new Date(v.dateTo).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                  </span>
                  {v.vacationType && (
                    <span className={`vacation-type-tag${v.vacationType.isIllness ? ' illness' : ''}`}>
                      {v.vacationType.name}
                    </span>
                  )}
                  <button
                    className="vacation-edit-btn"
                    onClick={() => navigate(`/student-vacations/edit/${v.student.id}/${v.id}`)}
                    title="Edit vacation"
                  >
                    ✎
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default Dashboard; 